import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import DeptSwitcher, { ccDepartments } from '../components/ContactCenter/DeptSwitcher';
import { useAuth } from '../context/AuthContext';
import AgentTable from '../components/ContactCenter/AgentTable';
import { languageOptions, matchesLanguage } from '../components/ContactCenter/language';
import AgentDetail from '../components/ContactCenter/AgentDetail';
import ExportButton from '../components/ContactCenter/ExportButton';
import { fmtAsOf } from '../components/ContactCenter/format';
import { btn, c } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Agents — every Support agent's work over a window: Handled
// (each conversation once), handle time, hold and transfers, by the old
// Agents page's rules (services/ccAgentRead.js), from stored Genesys data.
// "Today vs month to date" puts both side by side, as the old page did.

const DATA_FLOOR = '2026-06-04';

// Pacific dates, formatted in the zone: toISOString() reads the UTC date and
// after 5 pm Pacific would say tomorrow. The old page computed "last month"
// in the browser's own zone.
function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}
const firstOfMonth = (ymd) => `${ymd.slice(0, 8)}01`;
const addDays = (ymd, n) => new Date(Date.parse(`${ymd}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
function lastMonth(today) {
  const [y, m] = today.split('-').map(Number);
  return {
    start: new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10),
  };
}

// The old Agents page's windows and its wording. "Today vs Last 7d" is the
// one this page was missing: month to date flattens a bad week, and on the
// 2nd of a month there is nothing to compare today against at all.
const MODES = [
  ['today-mtd', 'Today vs MTD'],
  ['today-7d', 'Today vs Last 7d'],
  ['mtd', 'MTD only'],
  ['last-month', 'Last Month'],
  ['custom', 'Custom'],
];

// The seven days ending yesterday: "last 7 days" beside a Today column that
// is still filling means the seven BEFORE today, or today is counted twice.
const last7 = (today) => ({ start: addDays(today, -7), end: addDays(today, -1) });

const fetchAgents = (params) => api.get('/contact-center/agents', { params });

export default function ContactCenterAgents() {
  const today = pacificToday();
  const { user } = useAuth();
  // The department whose agents are shown: the first this person holds that
  // carries the page, which is Support wherever they hold it.
  const [dept, setDept] = useState(() => ccDepartments(user, 'agents')[0]?.id || 'support');
  const [mode, setMode] = useState('today-mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [pod, setPod] = useState('');
  const [channel, setChannel] = useState('');
  const [language, setLanguage] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const main = mode === 'last-month' ? lastMonth(today)
    : mode === 'custom' ? (custom.start && custom.end ? custom : null)
      : mode === 'today-7d' ? last7(today)
        : { start: firstOfMonth(today), end: today };
  const mainQ = useQuery({
    queryKey: ['cc-agents', dept, main?.start, main?.end],
    queryFn: () => fetchAgents({ ...main, dept }),
    enabled: !!main,
    refetchInterval: 5 * 60 * 1000,
  });
  const todayQ = useQuery({
    queryKey: ['cc-agents', dept, today, today],
    queryFn: () => fetchAgents({ start: today, end: today, dept }),
    enabled: mode === 'today-mtd' || mode === 'today-7d',
    refetchInterval: 5 * 60 * 1000,
  });

  const d = mainQ.data;
  const periods = useMemo(() => {
    if (!d) return [];
    const byUser = (list) => new Map((list || []).map((a) => [a.userId, a]));
    const mainPeriod = { key: 'main', label: mode === 'today-7d' ? 'Last 7d' : d.range.label, byUser: byUser(d.agents) };
    if (mode !== 'today-mtd' && mode !== 'today-7d') return [mainPeriod];
    return [{ key: 'today', label: 'Today', byUser: byUser(todayQ.data?.agents) }, mainPeriod];
  }, [d, todayQ.data, mode]);

  const channels = useMemo(() => [...new Set((d?.agents || []).flatMap((a) => Object.keys(a.channels)))].sort(), [d]);
  const needle = search.trim().toLowerCase();
  const shown = (d?.agents || []).filter((a) => (!pod || a.pod === pod)
    && (!channel || (a.channels[channel] || 0) > 0)
    && matchesLanguage(a, language)
    && (!needle || (a.name || '').toLowerCase().includes(needle)));
  const activeToday = (mode === 'today-mtd' || mode === 'today-7d') && todayQ.data
    ? shown.filter((a) => todayQ.data.agents.some((t) => t.userId === a.userId && t.handled > 0)).length : null;
  const close = useCallback(() => setOpenId(null), []);

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title={d?.deptLabel && d.deptLabel !== 'Support' ? `Agent Performance · ${d.deptLabel}` : 'Agent Performance'}
        subtitle={d ? `${shown.length} agent${shown.length === 1 ? '' : 's'}${activeToday != null ? ` · ${activeToday} active today` : ''} · ${d.range.label}` : 'Support agent performance'}
        meta={d?.asOf ? <span style={s.chip}>Data as of {fmtAsOf(d.asOf)}</span> : null}
        actions={(
          <div style={s.controls}>
            <DeptSwitcher view="agents" value={dept} onChange={setDept} />
            <span style={s.controls} role="group" aria-label="Date range">
            {MODES.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setMode(key)} aria-pressed={mode === key}
                style={mode === key ? s.pillOn : s.pill}>{label}</button>
            ))}
            {mode === 'custom' && (
              <>
                <input id="cc-agents-start" type="date" aria-label="Start date" min={DATA_FLOOR} max={today}
                  value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                <input id="cc-agents-end" type="date" aria-label="End date" min={DATA_FLOOR} max={today}
                  value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
              </>
            )}
            </span>
          </div>
        )}
      />

      <div style={s.filters} role="group" aria-label="Filters">
        <select id="cc-agents-pod" aria-label="Pod" value={pod} onChange={(e) => setPod(e.target.value)} style={s.input}>
          <option value="">All pods</option>
          {(d?.pods || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select id="cc-agents-channel" aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)} style={s.input}>
          <option value="">All channels</option>
          {channels.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>
        <select id="cc-agents-language" aria-label="Language" value={language} onChange={(e) => setLanguage(e.target.value)} style={s.input}>
          <option value="">All languages</option>
          {languageOptions(d?.agents).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input id="cc-agents-search" type="search" aria-label="Search agents" placeholder="Search agents" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ ...s.input, minWidth: '180px' }} />
        {d && (
          <span style={{ marginLeft: 'auto' }}>
            <ExportButton path="/contact-center/agents/export"
              params={{ start: d.range.start, end: d.range.end, dept, pod, channel, q: search.trim() }}
              filename={`Contact-Center-Agents_${d.range.start}_${d.range.end}.xlsx`} />
          </span>
        )}
      </div>

      {mode === 'custom' && !main && <p style={s.muted}>Choose a start and an end date.</p>}
      {mainQ.isLoading && main && <LoadingSpinner />}
      {mainQ.error && <div style={s.error}>Could not load agents: {mainQ.error.message}</div>}
      {d?.range.clamped && (
        <div style={s.notice}>Contact Center data starts on June 4, 2026, when Athens moved to Genesys; the window starts there.</div>
      )}

      {d && periods.length > 0 && (
        <AgentTable agents={shown} periods={periods} onOpen={setOpenId} selectedId={openId} />
      )}
      {d && (
        <p style={s.foot}>
          Handled counts each conversation once: inbound calls, callbacks that connected, and email. AHT is
          voice and callback handle time over voice handled; an agent who only answered email shows email
          time. Hold is the average length of a hold. An agent&rsquo;s pod is their Genesys team; * marks
          an agent on no pod team, placed by the queue they handled most. ⚑ marks handle time adjusted for a leg Genesys left open for hours.
          Hand/Hr is Handled per hour On Queue. Efficiency is working time (On Queue less hold, training,
          coaching, outbound, meetings, supervisor-approved) over time logged in less break and lunch; green
          from 92%, amber from 87%. &ldquo;Incomplete&rdquo; means some of the agent&rsquo;s time is under
          presence codes Genesys does not yet show the portal. Adherence, QA and CSAT follow with their data.
        </p>
      )}

      {openId && d && <AgentDetail userId={openId} range={d.range} dept={dept} onClose={close} />}
    </div>
  );
}

const s = {
  page:     { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  controls: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  filters:  { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  pill:     { ...btn.secondaryXs },
  pillOn:   { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:    { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  chip:     { fontSize: '11px', color: c.textMuted },
  notice:   { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:    { fontSize: '13px', color: c.dangerText },
  muted:    { fontSize: '12px', color: c.textMuted, margin: 0 },
  foot:     { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
};
